import type {
  OnboardingProcessStage,
  OnboardingProcessStageSubmittedDocuments,
} from '@interfaces/a-organizar/onboardingCompanyInformation';

/** Status que ainda aguardam decisão do analista. */
const PENDING_STATUSES = new Set(['Pending', 'SubmittedForApproval']);

export interface StageMetrics {
  submitted: number;
  pending: number;
  approved: number;
  rejected: number;
}

export interface AnalysisMetrics extends StageMetrics {
  analyzed: number;
  approvedPercent: number;
  rejectedPercent: number;
}

const EMPTY_STAGE_METRICS: StageMetrics = {
  submitted: 0,
  pending: 0,
  approved: 0,
  rejected: 0,
};

export function isPendingDocument(document: OnboardingProcessStageSubmittedDocuments): boolean {
  return PENDING_STATUSES.has(document.statusAnalysisDocuments);
}

export function toPercent(value: number, total: number): number {
  return total > 0 ? Math.round((value / total) * 100) : 0;
}

/** Fração percentual sem arredondar — para largura de barra, onde arredondar faria os segmentos não fecharem 100%. */
export function toFraction(value: number, total: number): number {
  return total > 0 ? (value / total) * 100 : 0;
}

export function collectStageDocuments(
  stage: OnboardingProcessStage
): OnboardingProcessStageSubmittedDocuments[] {
  return (stage.requiredDocuments ?? []).flatMap(required => required.submittedDocuments ?? []);
}

/**
 * Ids dos documentos submetidos que ainda aguardam decisão, na ordem em que aparecem na tela.
 *
 * É o conjunto que a ação "Aprovar todos" confirma e envia ao backend. Manda-se a lista, e não só
 * o companyId, para que a ação valha exatamente sobre o que o analista viu: documento cujo status
 * mudou entre a abertura da tela e o clique volta do servidor como `Skipped`, em vez de ser
 * aprovado às cegas.
 */
export function collectPendingDocumentIds(
  stages: OnboardingProcessStage[] | null | undefined
): string[] {
  return (stages ?? [])
    .flatMap(collectStageDocuments)
    .filter(isPendingDocument)
    .map(document => document.id)
    .filter((id): id is string => !!id);
}

export function buildStageMetrics(stage: OnboardingProcessStage): StageMetrics {
  const documents = collectStageDocuments(stage);

  return {
    submitted: documents.length,
    pending: documents.filter(isPendingDocument).length,
    approved: documents.filter(document => document.statusAnalysisDocuments === 'Approved').length,
    rejected: documents.filter(document => document.statusAnalysisDocuments === 'Rejected').length,
  };
}

export function buildAnalysisMetrics(
  stages: OnboardingProcessStage[] | null | undefined
): AnalysisMetrics {
  const totals = (stages ?? []).reduce<StageMetrics>((accumulated, stage) => {
    const stageMetrics = buildStageMetrics(stage);

    return {
      submitted: accumulated.submitted + stageMetrics.submitted,
      pending: accumulated.pending + stageMetrics.pending,
      approved: accumulated.approved + stageMetrics.approved,
      rejected: accumulated.rejected + stageMetrics.rejected,
    };
  }, EMPTY_STAGE_METRICS);

  return {
    ...totals,
    analyzed: totals.approved + totals.rejected,
    approvedPercent: toPercent(totals.approved, totals.submitted),
    rejectedPercent: toPercent(totals.rejected, totals.submitted),
  };
}
